import apiClient from "@/app/api/client";
import type {
	CreateEmployeeInput,
	Employee,
	EmployeeCreatedResult,
	EmployeeDetail,
	UpdateEmployeeInput,
} from "../types";

interface SingleEmployeeResponse {
	success: true;
	data: Employee;
}

interface EmployeeCreatedResponse {
	success: true;
	data: EmployeeCreatedResult;
}

interface EmployeeDetailResponse {
	success: true;
	data: EmployeeDetail;
}

export const employeesService = {
	/** Javobda `{ employee, temporaryPassword }` — parol faqat shu javobda, BIR MARTA. */
	async create(input: CreateEmployeeInput): Promise<EmployeeCreatedResult> {
		const response = await apiClient.post<EmployeeCreatedResponse>("/employees", input);
		return response.data.data;
	},

	/**
	 * Vaqtinchalik parolni qayta beradi.
	 *
	 * ⚠️ Marshrut `users` resursida: `POST /users/{userId}/reset-password`
	 * (`users.routes.ts:112`, darvoza `users:update`). `employees.id`
	 * yuborilsa 404 keladi va foydalanuvchi «tizim ishlamayapti» deb
	 * o'ylardi — shuning uchun chaqiruvchi AYNAN `employee.userId` beradi.
	 *
	 * ⚠️ Javob maydoni `password` (`users.schemas.ts:92-98`), UI esa
	 * `temporaryPassword` kutadi (`TemporaryPasswordModal.tsx:38`) —
	 * moslash AYNAN shu yerda, chegarada bajariladi. To'g'ridan-to'g'ri
	 * uzatilsa modal BO'SH `undefined` ko'rsatib, hech narsa yiqilmasdi.
	 */
	async resetPassword(userId: string): Promise<string> {
		const response = await apiClient.post<{ success: true; data: { password: string } }>(
			`/users/${userId}/reset-password`
		);

		return response.data.data.password;
	},

	async update(id: string, input: UpdateEmployeeInput): Promise<Employee> {
		const response = await apiClient.patch<SingleEmployeeResponse>(`/employees/${id}`, input);
		return response.data.data;
	},

	/** Profil sahifasi — `EmployeeSchema` PLUS telefon va xavf maydonlari. */
	async getById(id: string): Promise<EmployeeDetail> {
		const response = await apiClient.get<EmployeeDetailResponse>(`/employees/${id}`);
		return response.data.data;
	},

	/**
	 * Profil rasmi — SINXRON enrollment (1b-2 spec §4): javob qaytganda rasm
	 * Face AI'da ham, `files` da ham bor. Rad etish 422, xizmat mavjud
	 * emasligi 503 bilan keladi va ikkalasi ham NORMAL natija — chaqiruvchi
	 * ularni turlicha ko'rsatadi (§10).
	 */
	async setPhoto(id: string, file: File): Promise<Employee> {
		const formData = new FormData();
		formData.append("file", file);

		const response = await apiClient.patch<SingleEmployeeResponse>(
			`/employees/${id}/photo`,
			formData,
			{ headers: { "Content-Type": "multipart/form-data" } }
		);

		return response.data.data;
	},

	async deletePhoto(id: string): Promise<void> {
		await apiClient.delete(`/employees/${id}/photo`);
	},

	/**
	 * Rasm tarkibi — xom bayt oqimi.
	 *
	 * ⚠️ `<img src>` bu marshrutga to'g'ridan-to'g'ri ishora qila olmaydi:
	 * access token faqat JS xotirasida (`CLAUDE.md`), ya'ni brauzerning o'z
	 * rasm so'rovi `Authorization` sarlavhasini olib chiqmaydi. Shuning uchun
	 * blob `apiClient` orqali olinadi va `URL.createObjectURL` bilan
	 * ko'rsatiladi — chaqiruvchi uni `revokeObjectURL` bilan bo'shatishi shart.
	 */
	async getPhotoBlob(id: string): Promise<Blob> {
		const response = await apiClient.get<Blob>(`/employees/${id}/photo`, {
			responseType: "blob",
		});

		return response.data;
	},
};
